import useSWR from 'swr';
import api from '../../services/api';

export interface ActivityLog {
  id: number;
  userId: number;
  entityType: string;
  entityId: number;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  changes?: {
    before?: any;
    after?: any;
  };
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  user?: {
    id: number;
    userEmail: string;
    userDisplayName?: string;
    userFirstName?: string;
    userLastName?: string;
  };
}

export interface ActivityLogFilters {
  userId?: number;
  entityType?: string;
  entityId?: number;
  action?: string;
  startDate?: string;
  endDate?: string;
  ipAddress?: string;
}

const fetcher = async (url: string) => {
  const response = await api.get(url);
  return response.data;
};

export const useActivityLogs = (filters?: ActivityLogFilters) => {
  const params = new URLSearchParams();
  if (filters?.userId) params.append('userId', filters.userId.toString());
  if (filters?.entityType) params.append('entityType', filters.entityType);
  if (filters?.entityId) params.append('entityId', filters.entityId.toString());
  if (filters?.action) params.append('action', filters.action);
  if (filters?.startDate) params.append('startDate', filters.startDate);
  if (filters?.endDate) params.append('endDate', filters.endDate);
  if (filters?.ipAddress?.trim()) params.append('ipAddress', filters.ipAddress.trim());

  const queryString = params.toString();
  const url = `/activity-logs${queryString ? `?${queryString}` : ''}`;

  const { data, error, isLoading, mutate } = useSWR<ActivityLog[]>(url, fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useActivityLogsByEntity = (entityType: string, entityId: number) => {
  const { data, error, isLoading, mutate } = useSWR<ActivityLog[]>(
    entityType && entityId ? `/activity-logs/entity/${entityType}/${entityId}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useActivityLogsByUser = (userId: number) => {
  const { data, error, isLoading, mutate } = useSWR<ActivityLog[]>(
    userId ? `/activity-logs/user/${userId}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useActivityLog = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<ActivityLog>(
    id ? `/activity-logs/${id}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data,
    isLoading,
    isError: error,
    mutate,
  };
};
